/**
 * Types for Surveys widget
 * Corresponds to backend tool: get_surveys
 */

export interface Survey {
  /** Campaign ID for the survey */
  campaignId: string;
  /** Survey title */
  title: string;
  /** Brand name offering the survey */
  brandName: string;
  /** Brand ID for fetching brand logo */
  brandId?: number;
  /** Brand logo URL */
  brandLogoUrl?: string;
  /** Survey description */
  description?: string;
  /** Reward amount in dollars */
  rewardAmount: number;
  /** Estimated time to complete in minutes */
  estimatedMinutes?: number;
  /** Number of questions */
  questionCount?: number;
  /** Survey expiration date */
  expiresAt?: string;
  /** Preview image URL for the survey */
  previewImageUrl?: string;
}

export interface SurveysWidgetState {
  // Container
  maxWidth: number;
  paddingX: number;
  paddingY: number;
  backgroundColor: string;
  borderRadius: number;

  // Header
  headerFontSize: number;
  headerFontWeight: "normal" | "medium" | "semibold" | "bold";
  headerColor: string;
  headerMarginBottom: number;

  // Card styling
  cardBgColor: string;
  cardBorderColor: string;
  cardBorderWidth: number;
  cardBorderRadius: number;
  cardPadding: number;
  cardGap: number;

  // Brand logo
  brandLogoSize: number;
  brandLogoBorderRadius: number;

  // Content
  titleFontSize: number;
  titleFontWeight: "normal" | "medium" | "semibold" | "bold";
  titleColor: string;
  descriptionFontSize: number;
  descriptionColor: string;
  brandNameFontSize: number;
  brandNameColor: string;

  // Meta info (time, questions)
  metaFontSize: number;
  metaColor: string;

  // Reward badge
  rewardFontSize: number;
  rewardFontWeight: "normal" | "medium" | "semibold" | "bold";
  rewardColor: string;
  rewardBgColor: string;
  rewardBorderRadius: number;

  // Survey badge
  surveyBadgeFontSize: number;
  surveyBadgeColor: string;
  surveyBadgeBgColor: string;

  // Empty state
  emptyIconSize: number;
  emptyTextFontSize: number;
  emptyTextColor: string;
}

export const defaultSurveysState: SurveysWidgetState = {
  // Container
  maxWidth: 600,
  paddingX: 0,
  paddingY: 0,
  backgroundColor: "transparent",
  borderRadius: 0,

  // Header
  headerFontSize: 16,
  headerFontWeight: "semibold",
  headerColor: "#1a1a1a",
  headerMarginBottom: 12,

  // Card styling
  cardBgColor: "#ffffff",
  cardBorderColor: "#e5e7eb",
  cardBorderWidth: 1,
  cardBorderRadius: 12,
  cardPadding: 16,
  cardGap: 12,

  // Brand logo
  brandLogoSize: 48,
  brandLogoBorderRadius: 8,

  // Content
  titleFontSize: 15,
  titleFontWeight: "semibold",
  titleColor: "#181d56", // Brandclub primary color
  descriptionFontSize: 13,
  descriptionColor: "#6b7280",
  brandNameFontSize: 13,
  brandNameColor: "#6b7280",

  // Meta info
  metaFontSize: 12,
  metaColor: "#9ca3af",

  // Reward badge
  rewardFontSize: 14,
  rewardFontWeight: "bold",
  rewardColor: "#059669",
  rewardBgColor: "#ecfdf5",
  rewardBorderRadius: 6,

  // Survey badge
  surveyBadgeFontSize: 11,
  surveyBadgeColor: "#1d4ed8",
  surveyBadgeBgColor: "#dbeafe",

  // Empty state
  emptyIconSize: 48,
  emptyTextFontSize: 14,
  emptyTextColor: "#6b7280",
};

export interface SurveysWidgetProps {
  /** Widget instance ID */
  widgetId: string;
  /** Display name for edit panel */
  displayName?: string;
  /** Surveys to display */
  surveys: Survey[];
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string;
  /** Callback when a survey is clicked */
  onSurveyClick?: (survey: Survey) => void;
  /** Custom header text */
  headerText?: string;
  /** Whether to show the header */
  showHeader?: boolean;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Additional class name */
  className?: string;
  /** Override children for custom rendering */
  children?: (state: SurveysWidgetState, surveys: Survey[]) => React.ReactNode;
}
